import React, { useState, useEffect, useRef, useCallback } from "react";
import axiosInstance from "../../../utils/axios/axiosInstance";
import loadingGif from '../../../assets/images/loading.gif';

const JobsList = () => {
  // Use ref to track if component is mounted
  const isMountedRef = useRef(true);

  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  
  // States for saved jobs tab
  const [currentView, setCurrentView] = useState('all'); // 'all' or 'saved'
  const [savedJobsList, setSavedJobsList] = useState([]);
  const [savedJobsListLoading, setSavedJobsListLoading] = useState(false);
  const [savedJobsListError, setSavedJobsListError] = useState(null);
  
  // States for job details modal
  const [selectedJob, setSelectedJob] = useState(null);
  const [jobDetailsLoading, setJobDetailsLoading] = useState(false);
  const [jobDetailsError, setJobDetailsError] = useState(null);
  const [showJobModal, setShowJobModal] = useState(false);
  
  // Application states
  const [applyLoading, setApplyLoading] = useState(false);
  const [withdrawLoading, setWithdrawLoading] = useState(false);
  
  // Track save/unsave loading for individual jobs
  const [saveToggleLoading, setSaveToggleLoading] = useState(new Set());

  // Cache for applied jobs IDs to avoid repeated API calls
  const [appliedJobsCache, setAppliedJobsCache] = useState(new Set());
  const [appliedJobsCacheLoaded, setAppliedJobsCacheLoaded] = useState(false);

  // Search and Filter States
  const [showFilters, setShowFilters] = useState(false);
  const [isFiltering, setIsFiltering] = useState(false);
  const [filterParams, setFilterParams] = useState({
    search: '',
    job_type: '',
    location: '',
    employer: ''
  });
  const [activeFilters, setActiveFilters] = useState({});

  // Image loading states
  const [imageLoadingStates, setImageLoadingStates] = useState({});
  const [showSkeletonLoaders, setShowSkeletonLoaders] = useState(false);

  // Skeleton Loader Component
  const SkeletonLoader = () => (
    <div style={{
      backgroundColor: 'white',
      border: '1px solid #e9ecef',
      borderRadius: '12px',
      padding: '24px',
      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
      marginBottom: '16px'
    }}>
      {/* Header skeleton */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
        <div style={{ flex: 1 }}>
          {/* Job title skeleton */}
          <div style={{
            height: '24px',
            backgroundColor: '#f0f0f0',
            borderRadius: '4px',
            marginBottom: '8px',
            width: '70%',
            background: 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)',
            backgroundSize: '200% 100%',
            animation: 'shimmer 1.5s infinite'
          }} />
          {/* Company skeleton */}
          <div style={{
            height: '18px',
            backgroundColor: '#f0f0f0',
            borderRadius: '4px',
            width: '40%',
            background: 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)',
            backgroundSize: '200% 100%',
            animation: 'shimmer 1.5s infinite'
          }} />
        </div>
        
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          {/* Button skeletons */}
          <div style={{
            height: '32px',
            width: '80px',
            backgroundColor: '#f0f0f0',
            borderRadius: '6px',
            background: 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)',
            backgroundSize: '200% 100%',
            animation: 'shimmer 1.5s infinite'
          }} />
          <div style={{
            height: '32px',
            width: '40px',
            backgroundColor: '#f0f0f0',
            borderRadius: '6px',
            background: 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)',
            backgroundSize: '200% 100%',
            animation: 'shimmer 1.5s infinite'
          }} />
          <div style={{
            height: '32px',
            width: '100px',
            backgroundColor: '#f0f0f0',
            borderRadius: '20px',
            background: 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)',
            backgroundSize: '200% 100%',
            animation: 'shimmer 1.5s infinite'
          }} />
        </div>
      </div>

      {/* Description skeleton */}
      <div style={{
        height: '16px',
        backgroundColor: '#f0f0f0',
        borderRadius: '4px',
        marginBottom: '8px',
        width: '100%',
        background: 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)',
        backgroundSize: '200% 100%',
        animation: 'shimmer 1.5s infinite'
      }} />
      <div style={{
        height: '16px',
        backgroundColor: '#f0f0f0',
        borderRadius: '4px',
        marginBottom: '16px',
        width: '80%',
        background: 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)',
        backgroundSize: '200% 100%',
        animation: 'shimmer 1.5s infinite'
      }} />

      {/* Meta info skeleton */}
      <div style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: '16px',
        alignItems: 'center'
      }}>
        {[1, 2, 3].map(i => (
          <div key={i} style={{
            height: '16px',
            width: '120px',
            backgroundColor: '#f0f0f0',
            borderRadius: '4px',
            background: 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)',
            backgroundSize: '200% 100%',
            animation: 'shimmer 1.5s infinite'
          }} />
        ))}
        <div style={{ marginLeft: 'auto' }}>
          <div style={{
            height: '16px',
            width: '80px',
            backgroundColor: '#f0f0f0',
            borderRadius: '4px',
            background: 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)',
            backgroundSize: '200% 100%',
            animation: 'shimmer 1.5s infinite'
          }} />
        </div>
      </div>
    </div>
  );

  // Enhanced Loading Component with Image
  const LoadingComponent = ({ message, subMessage }) => (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #E3F2FD 0%, #E8EAF6 100%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    }}>
      <div style={{ padding: '32px', textAlign: 'center', maxWidth: '400px' }}>
        {/* Enhanced loading animation */}
        <div style={{
          position: 'relative',
          display: 'inline-block',
          marginBottom: '24px'
        }}>
          {loadingGif ? (
            <div style={{ position: 'relative' }}>
              <img
                src={loadingGif}
                alt="Loading..."
                style={{ 
                  width: '80px', 
                  height: '80px',
                  borderRadius: '50%',
                  boxShadow: '0 4px 20px rgba(206, 85, 185, 0.3)'
                }}
              />
              {/* Pulsing ring around loading gif */}
              <div style={{
                position: 'absolute',
                top: '-8px',
                left: '-8px',
                width: '96px',
                height: '96px',
                border: '2px solid #ce55b9',
                borderRadius: '50%',
                animation: 'pulse 2s infinite'
              }} />
            </div>
          ) : (
            <div style={{
              width: '80px',
              height: '80px',
              border: '4px solid #f3f3f3',
              borderTop: '4px solid #ce55b9',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              margin: '0 auto'
            }} />
          )}
        </div>
        
        <div style={{ 
          fontSize: '20px', 
          color: '#333', 
          fontFamily: '"Saira", sans-serif',
          fontWeight: '600',
          marginBottom: '8px'
        }}>
          {message}
        </div>
        
        {subMessage && (
          <div style={{ 
            fontSize: '14px', 
            color: '#666', 
            fontFamily: '"Saira", sans-serif',
            lineHeight: '1.4'
          }}>
            {subMessage}
          </div>
        )}

        {/* Loading progress dots */}
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          gap: '4px',
          marginTop: '16px'
        }}>
          {[0, 1, 2].map(i => (
            <div
              key={i}
              style={{
                width: '8px',
                height: '8px',
                borderRadius: '50%',
                backgroundColor: '#ce55b9',
                animation: `bounce 1.4s ease-in-out ${i * 0.16}s infinite both`
              }}
            />
          ))}
        </div>
      </div>
    </div>
  );

  // Image loading handler
  const handleImageLoad = useCallback((jobId) => {
    setImageLoadingStates(prev => ({
      ...prev,
      [jobId]: false
    }));
  }, []);

  const handleImageError = useCallback((jobId) => {
    setImageLoadingStates(prev => ({
      ...prev,
      [jobId]: 'error'
    }));
  }, []);

  // Normalize saved jobs data to include missing fields
  const normalizeSavedJobsData = useCallback((savedJobsData, appliedJobIds = new Set()) => {
    return savedJobsData.map(job => ({
      ...job,
      // Since this is from saved jobs API, we know it's saved
      is_saved: job.is_saved !== undefined ? job.is_saved : 1,
      // Check if job is in applied jobs set, fallback to existing field or 0
      is_applied: job.is_applied !== undefined ? job.is_applied : (appliedJobIds.has(job.id) ? 1 : 0),
      // Ensure human readable dates exist
      created_at_human: job.created_at_human || job.posted || 'Recently posted',
      updated_at_human: job.updated_at_human || job.posted || 'Recently updated'
    }));
  }, []);

  // Fetch applied jobs to build cache for status checking
  const fetchAppliedJobsCache = useCallback(async () => {
    try {
      console.log('📡 Fetching applied jobs cache...');
      const response = await axiosInstance.get(`${process.env.REACT_APP_TOOLS_API_BASE_URL}v1/jobs/applied`);
      
      if (response.data && response.data.status === 200) {
        const appliedJobIds = new Set((response.data.data || []).map(job => job.id));
        setAppliedJobsCache(appliedJobIds);
        setAppliedJobsCacheLoaded(true);
        console.log('✅ Applied jobs cache loaded:', Array.from(appliedJobIds));
        return appliedJobIds;
      }
    } catch (err) {
      console.warn('⚠️ Could not fetch applied jobs cache (endpoint may not exist):', err);
      // If applied jobs endpoint doesn't exist, continue without cache
      setAppliedJobsCacheLoaded(true);
      return new Set();
    }
  }, []);

  // Enhanced fetch saved jobs list with normalization
  const fetchSavedJobsList = useCallback(async () => {
    if (!isMountedRef.current) return;

    try {
      setSavedJobsListLoading(true);
      setSavedJobsListError(null);
      setShowSkeletonLoaders(true);

      console.log('📡 Fetching saved jobs list from API...');
      
      // Fetch applied jobs cache first if not already loaded
      let currentAppliedJobIds = appliedJobsCache;
      if (!appliedJobsCacheLoaded) {
        currentAppliedJobIds = await fetchAppliedJobsCache();
      }
      
      const response = await axiosInstance.get(`${process.env.REACT_APP_TOOLS_API_BASE_URL}v1/jobs/saved`);
      
      if (!isMountedRef.current) return;

      console.log('📥 Saved Jobs List API Response:', response.data);

      if (response.data && response.data.status === 200) {
        const savedJobsData = response.data.data || [];
        
        // Normalize the data to include missing fields
        const normalizedSavedJobs = normalizeSavedJobsData(savedJobsData, currentAppliedJobIds);
        setSavedJobsList(normalizedSavedJobs);
        
        console.log('✅ Loaded and normalized saved jobs list:', normalizedSavedJobs);
        console.log('📊 Normalized Job Status Summary:', {
          total: normalizedSavedJobs.length,
          saved: normalizedSavedJobs.filter(job => job.is_saved === 1).length,
          applied: normalizedSavedJobs.filter(job => job.is_applied === 1).length
        });
        
      } else {
        throw new Error(response.data?.message || 'Failed to fetch saved jobs list');
      }
    } catch (err) {
      console.error('❌ Error fetching saved jobs list:', err);
      
      let errorMessage = 'Failed to load saved jobs list';
      if (err.response?.status === 404) {
        setSavedJobsList([]);
        console.log('ℹ️ No saved jobs found (404 - normal for new users)');
        return;
      } else if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.response) {
        errorMessage = `Server error: ${err.response.status} ${err.response.statusText}`;
      } else if (err.request) {
        errorMessage = 'Network error: Unable to connect to server';
      } else {
        errorMessage = err.message || 'Unknown error occurred';
      }
      
      if (isMountedRef.current) {
        setSavedJobsListError(errorMessage);
      }
    } finally {
      if (isMountedRef.current) {
        setSavedJobsListLoading(false);
        setShowSkeletonLoaders(false);
      }
    }
  }, [normalizeSavedJobsData, appliedJobsCache, appliedJobsCacheLoaded, fetchAppliedJobsCache]);

  // Fetch jobs with filters function (enhanced with image loading)
  const fetchJobsWithFilters = useCallback(async (page = 1, filters = {}) => {
    if (!isMountedRef.current) return;

    try {
      setLoading(true);
      setError(null);
      setShowSkeletonLoaders(true);

      // Prepare filter payload
      const filterPayload = {
        page: page,
        ...Object.fromEntries(
          Object.entries(filters).filter(([key, value]) => value && value !== '')
        )
      };

      const isUsingFilters = Object.keys(filterPayload).length > 1; // More than just 'page'
      
      console.log(`📡 ${isUsingFilters ? 'Filtering' : 'Fetching'} jobs (page ${page})...`, filterPayload);
      
      let response;
      if (isUsingFilters) {
        // Use filter API
        response = await axiosInstance.post(
          `${process.env.REACT_APP_TOOLS_API_BASE_URL}v1/jobs/filter`,
          filterPayload
        );
        setIsFiltering(true);
      } else {
        // Use regular jobs API
        response = await axiosInstance.get(
          `${process.env.REACT_APP_TOOLS_API_BASE_URL}v1/jobs?page=${page}`
        );
        setIsFiltering(false);
      }
      
      if (!isMountedRef.current) return;

      console.log('📥 API Response:', response.data);

      if (response.data && response.data.status === 200) {
        const jobsData = response.data.data;
        
        // Initialize image loading states for new jobs
        const newJobs = jobsData.data || [];
        const newImageStates = {};
        newJobs.forEach(job => {
          newImageStates[job.id] = job.company_logo ? true : false;
        });
        setImageLoadingStates(newImageStates);
        
        setJobs(newJobs);
        setPagination({
          current_page: jobsData.current_page,
          last_page: jobsData.last_page,
          per_page: jobsData.per_page,
          total: jobsData.total,
          from: jobsData.from,
          to: jobsData.to
        });
        setCurrentPage(jobsData.current_page);

        if (jobsData.data && jobsData.data.length === 0) {
          console.log('ℹ️ No jobs found for this search/filter');
        } else {
          // Count saved jobs from the response
          const savedCount = jobsData.data?.filter(job => job.is_saved === 1).length || 0;
          const appliedCount = jobsData.data?.filter(job => job.is_applied === 1).length || 0;
          
          console.log(`📊 Job Status Summary: ${jobsData.data?.length || 0} total, ${savedCount} saved, ${appliedCount} applied`);
        }
      } else {
        throw new Error(response.data?.message || 'Failed to fetch jobs');
      }
    } catch (err) {
      console.error('❌ Error fetching jobs:', err);
      
      let errorMessage = 'Failed to load jobs';
      if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.response) {
        errorMessage = `Server error: ${err.response.status} ${err.response.statusText}`;
      } else if (err.request) {
        errorMessage = 'Network error: Unable to connect to server';
      } else {
        errorMessage = err.message || 'Unknown error occurred';
      }
      
      if (isMountedRef.current) {
        setError(errorMessage);
      }
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
        setShowSkeletonLoaders(false);
      }
    }
  }, []);

  // Apply filters function
  const applyFilters = useCallback(async () => {
    const cleanFilters = Object.fromEntries(
      Object.entries(filterParams).filter(([key, value]) => value && value !== '')
    );
    
    setActiveFilters(cleanFilters);
    setCurrentPage(1);
    
    await fetchJobsWithFilters(1, cleanFilters);
  }, [filterParams, fetchJobsWithFilters]);

  // Clear filters function
  const clearFilters = useCallback(async () => {
    setFilterParams({
      search: '',
      job_type: '',
      location: '',
      employer: ''
    });
    setActiveFilters({});
    setCurrentPage(1);
    setIsFiltering(false);
    setShowFilters(false); // Close the accordion when clearing filters
    
    await fetchJobsWithFilters(1, {});
  }, [fetchJobsWithFilters]);

  // Update filter parameter
  const updateFilterParam = useCallback((key, value) => {
    setFilterParams(prev => ({
      ...prev,
      [key]: value
    }));
  }, []);

  // Get active filters count
  const getActiveFiltersCount = useCallback(() => {
    return Object.keys(activeFilters).length;
  }, [activeFilters]);

  // Fetch job details function (unchanged)
  const fetchJobDetails = useCallback(async (jobId) => {
    if (!isMountedRef.current) return;

    try {
      setJobDetailsLoading(true);
      setJobDetailsError(null);

      console.log(`📡 Fetching job details for ID: ${jobId}...`);
      
      const response = await axiosInstance.get(`${process.env.REACT_APP_TOOLS_API_BASE_URL}v1/jobs/${jobId}`);
      
      if (!isMountedRef.current) return;

      console.log('📥 Job Details API Response:', response.data);

      if (response.data && response.data.status === 200) {
        const jobData = response.data.data;
        
        // Ensure job details have normalized fields
        const normalizedJobData = {
          ...jobData,
          is_saved: jobData.is_saved !== undefined ? jobData.is_saved : 0,
          is_applied: jobData.is_applied !== undefined ? jobData.is_applied : 0,
          created_at_human: jobData.created_at_human || jobData.posted || 'Recently posted',
          updated_at_human: jobData.updated_at_human || jobData.posted || 'Recently updated'
        };
        
        console.log('📋 Job Details Retrieved:', {
          id: normalizedJobData.id,
          title: normalizedJobData.job_title,
          is_applied: normalizedJobData.is_applied,
          is_applied_type: typeof normalizedJobData.is_applied,
          is_saved: normalizedJobData.is_saved,
          is_saved_type: typeof normalizedJobData.is_saved,
          employer: normalizedJobData.employer
        });
        
        setSelectedJob(normalizedJobData);
        setShowJobModal(true);
        
      } else {
        throw new Error(response.data?.message || 'Failed to fetch job details');
      }
    } catch (err) {
      console.error('❌ Error fetching job details:', err);
      
      let errorMessage = 'Failed to load job details';
      if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.response) {
        errorMessage = `Server error: ${err.response.status} ${err.response.statusText}`;
      } else if (err.request) {
        errorMessage = 'Network error: Unable to connect to server';
      } else {
        errorMessage = err.message || 'Unknown error occurred';
      }
      
      if (isMountedRef.current) {
        setJobDetailsError(errorMessage);
      }
    } finally {
      if (isMountedRef.current) {
        setJobDetailsLoading(false);
      }
    }
  }, []);

  // Handle job card click
  const handleJobClick = useCallback((jobId) => {
    fetchJobDetails(jobId);
  }, [fetchJobDetails]);

  // Handle view change with cache loading
  const handleViewChange = useCallback((view) => {
    setCurrentView(view);
    if (view === 'saved') {
      fetchSavedJobsList();
    }
  }, [fetchSavedJobsList]);

  // Apply for job function with cache update
  const applyForJob = useCallback(async (jobId) => {
    if (!isMountedRef.current || !jobId) return;

    try {
      setApplyLoading(true);

      console.log(`📡 Applying for job ID: ${jobId}...`);
      
      const response = await axiosInstance.post(`${process.env.REACT_APP_TOOLS_API_BASE_URL}v1/jobs/${jobId}/apply`);
      
      if (!isMountedRef.current) return;

      console.log('📥 Job Application API Response:', response.data);

      if (response.data && response.data.status === 200) {
        // Update applied jobs cache
        setAppliedJobsCache(prev => new Set([...prev, jobId]));
        
        // Update local state for all jobs view
        if (currentView === 'all') {
          setJobs(prevJobs => 
            prevJobs.map(job => 
              job.id === jobId ? { ...job, is_applied: 1 } : job
            )
          );
        }
        
        // Update saved jobs list if job is in it
        if (currentView === 'saved') {
          setSavedJobsList(prevJobs => 
            prevJobs.map(job => 
              job.id === jobId ? { ...job, is_applied: 1 } : job
            )
          );
        }
        
        // Update selected job in modal
        if (selectedJob && selectedJob.id === jobId) {
          setSelectedJob(prev => ({ ...prev, is_applied: 1 }));
        }
        
        console.log('✅ Successfully applied for job and updated local state');
      } else {
        throw new Error(response.data?.message || 'Failed to submit job application');
      }
    } catch (err) {
      console.error('❌ Error applying for job:', err);
      
      if (err.response?.status === 409) {
        console.log('ℹ️ Job already applied, syncing local state...');
        
        // Update applied jobs cache even on conflict
        setAppliedJobsCache(prev => new Set([...prev, jobId]));
        
        // Update local state even on conflict (409) - user already applied
        if (currentView === 'all') {
          setJobs(prevJobs => 
            prevJobs.map(job => 
              job.id === jobId ? { ...job, is_applied: 1 } : job
            )
          );
        }
        
        if (currentView === 'saved') {
          setSavedJobsList(prevJobs => 
            prevJobs.map(job => 
              job.id === jobId ? { ...job, is_applied: 1 } : job
            )
          );
        }
        
        if (selectedJob && selectedJob.id === jobId) {
          setSelectedJob(prev => ({ ...prev, is_applied: 1 }));
        }
        
      } else {
        let errorMessage = 'Failed to submit job application';
        if (err.response?.data?.message) {
          errorMessage = err.response.data.message;
        } else if (err.response) {
          errorMessage = `Server error: ${err.response.status} ${err.response.statusText}`;
        } else if (err.request) {
          errorMessage = 'Network error: Unable to connect to server';
        } else {
          errorMessage = err.message || 'Unknown error occurred';
        }
        
        console.error('Application failed:', errorMessage);
      }
    } finally {
      if (isMountedRef.current) {
        setApplyLoading(false);
      }
    }
  }, [currentView, selectedJob]);

  // Withdraw job application function with cache update
  const withdrawJobApplication = useCallback(async (jobId) => {
    if (!isMountedRef.current || !jobId) return;

    try {
      setWithdrawLoading(true);

      console.log(`📡 Withdrawing job application for ID: ${jobId}...`);
      
      const response = await axiosInstance.post(`${process.env.REACT_APP_TOOLS_API_BASE_URL}v1/jobs/${jobId}/withdraw`);
      
      if (!isMountedRef.current) return;

      console.log('📥 Job Withdrawal API Response:', response.data);

      if (response.data && response.data.status === 200) {
        // Update applied jobs cache
        setAppliedJobsCache(prev => {
          const newSet = new Set(prev);
          newSet.delete(jobId);
          return newSet;
        });
        
        // Update local state for all jobs view
        if (currentView === 'all') {
          setJobs(prevJobs => 
            prevJobs.map(job => 
              job.id === jobId ? { ...job, is_applied: 0 } : job
            )
          );
        }
        
        // Update saved jobs list if job is in it
        if (currentView === 'saved') {
          setSavedJobsList(prevJobs => 
            prevJobs.map(job => 
              job.id === jobId ? { ...job, is_applied: 0 } : job
            )
          );
        }
        
        // Update selected job in modal
        if (selectedJob && selectedJob.id === jobId) {
          setSelectedJob(prev => ({ ...prev, is_applied: 0 }));
        }
        
        console.log('✅ Successfully withdrew job application and updated local state');
      } else {
        throw new Error(response.data?.message || 'Failed to withdraw job application');
      }
    } catch (err) {
      console.error('❌ Error withdrawing job application:', err);
      
      let errorMessage = 'Failed to withdraw job application';
      
      if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.response) {
        errorMessage = `Server error: ${err.response.status} ${err.response.statusText}`;
      } else if (err.request) {
        errorMessage = 'Network error: Unable to connect to server';
      } else {
        errorMessage = err.message || 'Unknown error occurred';
      }
      
      console.error('Withdrawal failed:', errorMessage);
    } finally {
      if (isMountedRef.current) {
        setWithdrawLoading(false);
      }
    }
  }, [currentView, selectedJob]);

  // Toggle save/unsave job function (unchanged)
  const toggleSaveJob = useCallback(async (jobId) => {
    if (!isMountedRef.current || !jobId) return;

    // Find current job to determine if it's saved
    const currentJob = currentView === 'all' 
      ? jobs.find(job => job.id === jobId)
      : savedJobsList.find(job => job.id === jobId) || selectedJob;
    
    const wasSaved = currentJob?.is_saved === 1;

    try {
      // Add this job to loading set
      setSaveToggleLoading(prev => new Set([...prev, jobId]));
      
      console.log(`📡 ${wasSaved ? 'Unsaving' : 'Saving'} job ID: ${jobId}...`);
      
      // Optimistic update for all jobs view
      if (currentView === 'all') {
        setJobs(prevJobs => 
          prevJobs.map(job => 
            job.id === jobId ? { ...job, is_saved: wasSaved ? 0 : 1 } : job
          )
        );
      }
      
      // Optimistic update for saved jobs view (remove if unsaving)
      if (currentView === 'saved' && wasSaved) {
        setSavedJobsList(prev => prev.filter(job => job.id !== jobId));
      }
      
      // Update selected job in modal
      if (selectedJob && selectedJob.id === jobId) {
        setSelectedJob(prev => ({ ...prev, is_saved: wasSaved ? 0 : 1 }));
      }
      
      const response = await axiosInstance.post(`${process.env.REACT_APP_TOOLS_API_BASE_URL}v1/jobs/${jobId}/save`);
      
      if (!isMountedRef.current) return;

      console.log('📥 Job Save/Unsave API Response:', response.data);

      if (response.data && response.data.status === 200) {
        console.log(`✅ Successfully ${wasSaved ? 'unsaved' : 'saved'} job`);
        
        // If we're in saved view and this was a save operation, refresh to show new job
        if (currentView === 'saved' && !wasSaved) {
          fetchSavedJobsList();
        }
      } else {
        // Revert optimistic update on API failure
        if (currentView === 'all') {
          setJobs(prevJobs => 
            prevJobs.map(job => 
              job.id === jobId ? { ...job, is_saved: wasSaved ? 1 : 0 } : job
            )
          );
        }
        
        if (currentView === 'saved' && wasSaved) {
          // Re-add to saved jobs list if it was optimistically removed
          if (currentJob) {
            setSavedJobsList(prev => [...prev, { ...currentJob, is_saved: 1 }]);
          }
        }
        
        if (selectedJob && selectedJob.id === jobId) {
          setSelectedJob(prev => ({ ...prev, is_saved: wasSaved ? 1 : 0 }));
        }
        
        throw new Error(response.data?.message || 'Failed to update job save status');
      }
    } catch (err) {
      console.error('❌ Error toggling job save status:', err);
      
      // Revert optimistic update on error
      if (currentView === 'all') {
        setJobs(prevJobs => 
          prevJobs.map(job => 
            job.id === jobId ? { ...job, is_saved: wasSaved ? 1 : 0 } : job
          )
        );
      }
      
      if (currentView === 'saved' && wasSaved) {
        // Re-add to saved jobs list if it was optimistically removed
        if (currentJob) {
          setSavedJobsList(prev => [...prev, { ...currentJob, is_saved: 1 }]);
        }
      }
      
      if (selectedJob && selectedJob.id === jobId) {
        setSelectedJob(prev => ({ ...prev, is_saved: wasSaved ? 1 : 0 }));
      }
      
      let errorMessage = 'Failed to update job save status';
      
      if (err.response?.status === 400 || err.response?.status === 409) {
        errorMessage = err.response.data?.message || 'Job save status could not be updated.';
      } else {
        if (err.response?.data?.message) {
          errorMessage = err.response.data.message;
        } else if (err.response) {
          errorMessage = `Server error: ${err.response.status} ${err.response.statusText}`;
        } else if (err.request) {
          errorMessage = 'Network error: Unable to connect to server';
        } else {
          errorMessage = err.message || 'Unknown error occurred';
        }
      }
      
      console.error('Save toggle failed:', errorMessage);
    } finally {
      if (isMountedRef.current) {
        // Remove from loading set
        setSaveToggleLoading(prev => {
          const newSet = new Set(prev);
          newSet.delete(jobId);
          return newSet;
        });
      }
    }
  }, [jobs, savedJobsList, selectedJob, currentView, fetchSavedJobsList]);

  // Close job modal
  const closeJobModal = useCallback(() => {
    setShowJobModal(false);
    setSelectedJob(null);
    setJobDetailsError(null);
  }, []);

  // Check if current job has been applied to with better null handling
  const hasAppliedToJob = useCallback((job) => {
    if (!job) return false;
    return job.is_applied === 1 || job.is_applied === "1" || job.is_applied === true;
  }, []);

  // Check if current job has been saved with better null handling
  const hasSavedCurrentJob = useCallback((job) => {
    if (!job) return false;
    return job.is_saved === 1 || job.is_saved === "1" || job.is_saved === true;
  }, []);

  // Check if job is currently being toggled
  const isJobSaveLoading = useCallback((jobId) => {
    return saveToggleLoading.has(jobId);
  }, [saveToggleLoading]);

  // Get saved jobs count from current jobs list
  const getSavedJobsCount = useCallback(() => {
    if (currentView === 'all') {
      return jobs.filter(job => job.is_saved === 1).length;
    }
    return savedJobsList.length;
  }, [jobs, savedJobsList, currentView]);

  // Initialize component with cache loading
  useEffect(() => {
    isMountedRef.current = true;

    const fetchData = async () => {
      try {
        if (currentView === 'all') {
          await fetchJobsWithFilters(1, {});
        } else if (currentView === 'saved') {
          await fetchSavedJobsList();
        }
      } catch (error) {
        console.error('Failed to fetch jobs:', error);
      }
    };

    fetchData();

    return () => {
      isMountedRef.current = false;
    };
  }, [fetchJobsWithFilters, fetchSavedJobsList, currentView]);

  // Handle page change
  const handlePageChange = useCallback((page) => {
    if (page !== currentPage && page >= 1 && page <= pagination?.last_page) {
      fetchJobsWithFilters(page, activeFilters);
    }
  }, [currentPage, pagination, fetchJobsWithFilters, activeFilters]);

  // Refresh function
  const refreshData = useCallback(async () => {
    try {
      if (currentView === 'all') {
        await fetchJobsWithFilters(currentPage, activeFilters);
      } else if (currentView === 'saved') {
        // Reset cache and reload
        setAppliedJobsCacheLoaded(false);
        setAppliedJobsCache(new Set());
        await fetchSavedJobsList();
      }
    } catch (error) {
      console.error('Failed to refresh data:', error);
    }
  }, [currentPage, fetchJobsWithFilters, fetchSavedJobsList, currentView, activeFilters]);

  // Get job type label
  const getJobTypeLabel = (type) => {
    const types = { 1: 'Full Time', 2: 'Part Time', 3: 'Contract', 4: 'Internship', '1': 'Full Time', '2': 'Part Time', '3': 'Contract', '4': 'Internship' };
    return types[type] || 'Unknown';
  };

  // UPDATED: Get salary range label - now displays raw API values
  const getSalaryRangeLabel = (range) => {
    // Return the raw value from API if it exists
    if (range && range.toString().trim() !== '') {
      return range.toString();
    }
    
    return 'Not specified';
  };

  // Cleanup effect
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Loading state
  const isAnyLoading = loading || savedJobsListLoading;
  
  if (isAnyLoading && (currentView === 'all' ? jobs.length === 0 : savedJobsList.length === 0)) {
    return (
      <LoadingComponent 
        message={currentView === 'saved' ? 'Loading saved jobs...' : 'Loading job listings...'}
        subMessage={currentView === 'saved' && !appliedJobsCacheLoaded ? 'Checking application status...' : 'Please wait while we fetch the latest opportunities'}
      />
    );
  }

  // Error state
  const currentError = currentView === 'all' ? error : savedJobsListError;
  const currentData = currentView === 'all' ? jobs : savedJobsList;
  
  if (currentError && !currentData.length) {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #E3F2FD 0%, #E8EAF6 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div style={{ padding: '32px', maxWidth: '400px', margin: '0 16px', textAlign: 'center' }}>
          <div style={{ fontSize: '48px', color: '#f44336', marginBottom: '16px' }}>❌</div>
          <div style={{ 
            padding: '16px', 
            backgroundColor: '#ffebee', 
            border: '1px solid #f44336',
            borderRadius: '8px',
            marginBottom: '24px',
            fontFamily: '"Saira", sans-serif'
          }}>
            <strong>Error!</strong> {currentError}
          </div>
          <button
            onClick={refreshData}
            style={{
              padding: '12px 24px',
              backgroundColor: '#1976d2',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '16px',
              fontFamily: '"Saira", sans-serif'
            }}
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
          
          @keyframes pulse {
            0% { transform: scale(1); opacity: 1; }
            50% { transform: scale(1.05); opacity: 0.7; }
            100% { transform: scale(1); opacity: 1; }
          }
          
          @keyframes bounce {
            0%, 80%, 100% { transform: scale(0); }
            40% { transform: scale(1); }
          }
          
          @keyframes shimmer {
            0% { background-position: -200% 0; }
            100% { background-position: 200% 0; }
          }
          
          @keyframes slideInRight {
            from {
              transform: translateX(100%);
              opacity: 0;
            }
            to {
              transform: translateX(0);
              opacity: 1;
            }
          }
          
          @keyframes slideOutRight {
            from {
              transform: translateX(0);
              opacity: 1;
            }
            to {
              transform: translateX(100%);
              opacity: 0;
            }
          }
          
          @keyframes expandAccordion {
            from {
              max-height: 0;
              opacity: 0;
              transform: translateY(-10px);
            }
            to {
              max-height: 500px;
              opacity: 1;
              transform: translateY(0);
            }
          }
          
          .slide-overlay {
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background-color: rgba(0, 0, 0, 0.5);
            z-index: 1000;
            animation: fadeIn 0.3s ease-out;
          }
          
          .slide-panel {
            position: fixed;
            top: 0;
            right: 0;
            width: 60%;
            max-width: 800px;
            min-width: 400px;
            height: 100vh;
            background-color: white;
            box-shadow: -4px 0 20px rgba(0, 0, 0, 0.15);
            animation: slideInRight 0.3s ease-out;
            display: flex;
            flex-direction: column;
          }
          
          .slide-panel.closing {
            animation: slideOutRight 0.3s ease-out;
          }
          
          @keyframes fadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
          }
          
          @media (max-width: 768px) {
            .slide-panel {
              width: 90%;
              min-width: 300px;
            }
          }
          
          @media (max-width: 480px) {
            .slide-panel {
              width: 100%;
              min-width: unset;
            }
          }
        `}
      </style>
      
      <div style={{ padding: '16px' }}>
        <div className="container">
          <div className="css-11ezll3-MuiToolbar-root breadcrumbs">
            <div className="top-sec">
              <div className="card custom-card">
                <div className="card-body">
                  {/* Error display */}
                  {currentError && currentData.length > 0 && (
                    <div style={{
                      padding: '16px',
                      backgroundColor: '#ffebee',
                      border: '1px solid #f44336',
                      borderRadius: '8px',
                      marginBottom: '24px',
                      fontFamily: '"Saira", sans-serif',
                      color: '#c62828'
                    }}>
                      <strong>Error!</strong> {currentError}
                      <button
                        onClick={() => {
                          if (currentView === 'all') {
                            setError(null);
                          } else {
                            setSavedJobsListError(null);
                          }
                        }}
                        style={{
                          float: 'right',
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          fontSize: '18px',
                          color: '#c62828'
                        }}
                      >
                        ×
                      </button>
                    </div>
                  )}

                  {/* Header Section with Tabs */}
                  <div className="d-flex align-items-center justify-content-between flex-wrap pb-1">
                    <div className="d-flex align-items-center">
                      <h3 className="mb-2" style={{ fontFamily: '"Saira", sans-serif' }}>
                        {currentView === 'all' ? 'Jobs List' : 'Saved Jobs'}
                        {isAnyLoading && <span style={{ marginLeft: '8px', fontSize: '16px' }}>⏳</span>}
                        {isFiltering && currentView === 'all' && (
                          <span style={{ 
                            fontSize: '12px', 
                            backgroundColor: '#ce55b9', 
                            color: 'white',
                            padding: '2px 8px',
                            borderRadius: '12px',
                            marginLeft: '8px',
                            fontWeight: '500'
                          }}>
                            FILTERED
                          </span>
                        )}
                        {currentView === 'all' && pagination && (
                          <span style={{ 
                            fontSize: '16px', 
                            color: '#666', 
                            fontWeight: 'normal',
                            marginLeft: '12px'
                          }}>
                            ({pagination.total} jobs found)
                          </span>
                        )}
                        {currentView === 'saved' && (
                          <span style={{ 
                            fontSize: '16px', 
                            color: '#666', 
                            fontWeight: 'normal',
                            marginLeft: '12px'
                          }}>
                            ({savedJobsList.length} saved jobs)
                          </span>
                        )}
                      </h3>
                    </div>
                    
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                      {/* View Toggle Tabs */}
                      <div style={{
                        display: 'flex',
                        backgroundColor: '#f8f9fa',
                        borderRadius: '8px',
                        padding: '4px',
                        marginRight: '12px'
                      }}>
                        <button
                          onClick={() => handleViewChange('all')}
                          style={{
                            padding: '8px 16px',
                            backgroundColor: currentView === 'all' ? '#ce55b9' : 'transparent',
                            color: currentView === 'all' ? 'white' : '#666',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontFamily: '"Saira", sans-serif',
                            fontSize: '14px',
                            fontWeight: '500',
                            transition: 'all 0.2s ease'
                          }}
                        >
                          All Jobs
                        </button>
                        <button
                          onClick={() => handleViewChange('saved')}
                          style={{
                            padding: '8px 16px',
                            backgroundColor: currentView === 'saved' ? '#ce55b9' : 'transparent',
                            color: currentView === 'saved' ? 'white' : '#666',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontFamily: '"Saira", sans-serif',
                            fontSize: '14px',
                            fontWeight: '500',
                            transition: 'all 0.2s ease',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px'
                          }}
                        >
                          💾 Saved Jobs
                          {getSavedJobsCount() > 0 && (
                            <span style={{
                              backgroundColor: currentView === 'saved' ? 'rgba(255,255,255,0.3)' : '#ce55b9',
                              color: currentView === 'saved' ? 'white' : 'white',
                              borderRadius: '10px',
                              padding: '2px 6px',
                              fontSize: '12px',
                              minWidth: '16px',
                              textAlign: 'center'
                            }}>
                              {getSavedJobsCount()}
                            </span>
                          )}
                        </button>
                      </div>

                      {/* Search & Filter Button (only for All Jobs view) */}
                      {currentView === 'all' && (
                        <button
                          onClick={() => setShowFilters(true)}
                          style={{
                            backgroundColor: getActiveFiltersCount() > 0 ? '#ce55b9' : '#f8f9fa',
                            color: getActiveFiltersCount() > 0 ? 'white' : '#666',
                            border: '1px solid #ddd',
                            borderRadius: '8px',
                            padding: '8px 16px',
                            cursor: 'pointer',
                            fontFamily: '"Saira", sans-serif',
                            fontSize: '14px',
                            fontWeight: '500',
                            transition: 'all 0.2s ease',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                          }}
                        >
                          🔍 Search & Filter
                          {getActiveFiltersCount() > 0 && (
                            <span style={{
                              backgroundColor: 'rgba(255,255,255,0.3)',
                              borderRadius: '10px',
                              padding: '2px 6px',
                              fontSize: '12px',
                              minWidth: '16px',
                              textAlign: 'center'
                            }}>
                              {getActiveFiltersCount()}
                            </span>
                          )}
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Accordion-style Search & Filter Section */}
                  {showFilters && currentView === 'all' && (
                    <div 
                      style={{
                        backgroundColor: '#f8f9fa',
                        border: '1px solid #e9ecef',
                        borderRadius: '12px',
                        marginBottom: '24px',
                        overflow: 'hidden',
                        transition: 'all 0.3s ease',
                        animation: 'expandAccordion 0.3s ease-out'
                      }}
                    >
                      {/* Filter Header */}
                      <div style={{
                        background: 'linear-gradient(135deg, #ce55b9 0%, #b8479a 100%)',
                        color: 'white',
                        padding: '16px 20px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        cursor: 'pointer'
                      }}
                      onClick={() => setShowFilters(false)}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                          <div style={{
                            width: '32px',
                            height: '32px',
                            background: 'rgba(255, 255, 255, 0.15)',
                            borderRadius: '8px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '16px'
                          }}>
                            🔍
                          </div>
                          <div>
                            <h3 style={{
                              margin: 0,
                              fontSize: '18px',
                              fontWeight: '600',
                              fontFamily: '"Saira", sans-serif'
                            }}>
                              Search & Filter Jobs
                            </h3>
                            <p style={{
                              margin: 0,
                              fontSize: '12px',
                              opacity: 0.9,
                              fontFamily: '"Saira", sans-serif'
                            }}>
                              Find the perfect job for you
                            </p>
                          </div>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                          {getActiveFiltersCount() > 0 && (
                            <span style={{
                              backgroundColor: 'rgba(255,255,255,0.2)',
                              borderRadius: '12px',
                              padding: '4px 8px',
                              fontSize: '12px',
                              fontWeight: '500'
                            }}>
                              {getActiveFiltersCount()} active
                            </span>
                          )}
                          <button
                            style={{
                              background: 'rgba(255, 255, 255, 0.15)',
                              border: 'none',
                              color: 'white',
                              fontSize: '16px',
                              cursor: 'pointer',
                              padding: '6px',
                              borderRadius: '4px',
                              width: '28px',
                              height: '28px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              transition: 'all 0.2s ease',
                              transform: 'rotate(180deg)'
                            }}
                            onMouseEnter={(e) => (e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.25)')}
                            onMouseLeave={(e) => (e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.15)')}
                          >
                            ▼
                          </button>
                        </div>
                      </div>

                      {/* Filter Content */}
                      <div style={{ padding: '20px' }}>
                        {/* Search Bar */}
                        <div style={{ marginBottom: '20px' }}>
                          <label style={{
                            display: 'block',
                            fontSize: '14px',
                            fontWeight: '600',
                            color: '#333',
                            marginBottom: '8px',
                            fontFamily: '"Saira", sans-serif'
                          }}>
                            Search Keywords
                          </label>
                          <input
                            type="text"
                            placeholder="Search by job title, company, or description..."
                            value={filterParams.search}
                            onChange={(e) => updateFilterParam('search', e.target.value)}
                            style={{
                              width: '100%',
                              padding: '12px 16px',
                              border: '1px solid #ddd',
                              borderRadius: '8px',
                              fontSize: '16px',
                              fontFamily: '"Saira", sans-serif',
                              outline: 'none',
                              transition: 'border-color 0.2s ease',
                              boxSizing: 'border-box'
                            }}
                            onFocus={(e) => (e.target.style.borderColor = '#ce55b9')}
                            onBlur={(e) => (e.target.style.borderColor = '#ddd')}
                          />
                        </div>

                        {/* Filter Grid */}
                        <div style={{
                          display: 'grid',
                          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                          gap: '16px',
                          marginBottom: '20px'
                        }}>
                          {/* Job Type Filter */}
                          <div>
                            <label style={{
                              display: 'block',
                              fontSize: '14px',
                              fontWeight: '600',
                              color: '#333',
                              marginBottom: '8px',
                              fontFamily: '"Saira", sans-serif'
                            }}>
                              Job Type
                            </label>
                            <select
                              value={filterParams.job_type}
                              onChange={(e) => updateFilterParam('job_type', e.target.value)}
                              style={{
                                width: '100%',
                                padding: '12px 16px',
                                border: '1px solid #ddd',
                                borderRadius: '8px',
                                fontSize: '14px',
                                fontFamily: '"Saira", sans-serif',
                                outline: 'none',
                                backgroundColor: 'white',
                                cursor: 'pointer',
                                boxSizing: 'border-box'
                              }}
                            >
                              <option value="">All Job Types</option>
                              <option value="1">Full Time</option>
                              <option value="2">Part Time</option>
                              <option value="3">Contract</option>
                              <option value="4">Internship</option>
                            </select>
                          </div>

                          {/* Location Filter */}
                          <div>
                            <label style={{
                              display: 'block',
                              fontSize: '14px',
                              fontWeight: '600',
                              color: '#333',
                              marginBottom: '8px',
                              fontFamily: '"Saira", sans-serif'
                            }}>
                              Location
                            </label>
                            <input
                              type="text"
                              placeholder="Enter city or location..."
                              value={filterParams.location}
                              onChange={(e) => updateFilterParam('location', e.target.value)}
                              style={{
                                width: '100%',
                                padding: '12px 16px',
                                border: '1px solid #ddd',
                                borderRadius: '8px',
                                fontSize: '14px',
                                fontFamily: '"Saira", sans-serif',
                                outline: 'none',
                                transition: 'border-color 0.2s ease',
                                boxSizing: 'border-box'
                              }}
                              onFocus={(e) => (e.target.style.borderColor = '#ce55b9')}
                              onBlur={(e) => (e.target.style.borderColor = '#ddd')}
                            />
                          </div>

                          {/* Company Filter */}
                          <div>
                            <label style={{
                              display: 'block',
                              fontSize: '14px',
                              fontWeight: '600',
                              color: '#333',
                              marginBottom: '8px',
                              fontFamily: '"Saira", sans-serif'
                            }}>
                              Company
                            </label>
                            <input
                              type="text"
                              placeholder="Enter company name..."
                              value={filterParams.employer}
                              onChange={(e) => updateFilterParam('employer', e.target.value)}
                              style={{
                                width: '100%',
                                padding: '12px 16px',
                                border: '1px solid #ddd',
                                borderRadius: '8px',
                                fontSize: '14px',
                                fontFamily: '"Saira", sans-serif',
                                outline: 'none',
                                transition: 'border-color 0.2s ease',
                                boxSizing: 'border-box'
                              }}
                              onFocus={(e) => (e.target.style.borderColor = '#ce55b9')}
                              onBlur={(e) => (e.target.style.borderColor = '#ddd')}
                            />
                          </div>
                        </div>

                        {/* Active Filters Display */}
                        {getActiveFiltersCount() > 0 && (
                          <div style={{
                            backgroundColor: '#e3f2fd',
                            border: '1px solid #bbdefb',
                            borderRadius: '8px',
                            padding: '12px',
                            marginBottom: '20px'
                          }}>
                            <h4 style={{
                              fontSize: '13px',
                              fontWeight: '600',
                              color: '#333',
                              marginBottom: '8px',
                              fontFamily: '"Saira", sans-serif'
                            }}>
                              Active Filters ({getActiveFiltersCount()})
                            </h4>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                              {Object.entries(activeFilters).map(([key, value]) => (
                                <span
                                  key={key}
                                  style={{
                                    backgroundColor: '#ce55b9',
                                    color: 'white',
                                    padding: '3px 10px',
                                    borderRadius: '14px',
                                    fontSize: '11px',
                                    fontWeight: '500',
                                    fontFamily: '"Saira", sans-serif',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '4px'
                                  }}
                                >
                                  {key === 'job_type' ? getJobTypeLabel(value) :
                                   key === 'salary_range' ? value : // Display salary range string directly
                                   key === 'search' ? `"${value}"` :
                                   value}
                                  <button
                                    onClick={async () => {
                                      // Update filter params by removing this specific filter
                                      const newFilters = { ...filterParams };
                                      newFilters[key] = '';
                                      setFilterParams(newFilters);
                                      
                                      // Update active filters by removing this key
                                      const newActiveFilters = { ...activeFilters };
                                      delete newActiveFilters[key];
                                      setActiveFilters(newActiveFilters);
                                      
                                      // Apply the updated filters immediately
                                      setCurrentPage(1);
                                      await fetchJobsWithFilters(1, newActiveFilters);
                                    }}
                                    style={{
                                      background: 'rgba(255,255,255,0.3)',
                                      border: 'none',
                                      color: 'white',
                                      borderRadius: '50%',
                                      width: '14px',
                                      height: '14px',
                                      fontSize: '10px',
                                      cursor: 'pointer',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center'
                                    }}
                                  >
                                    ×
                                  </button>
                                </span>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Action Buttons */}
                        <div style={{
                          display: 'flex',
                          gap: '12px',
                          justifyContent: 'center',
                          flexWrap: 'wrap'
                        }}>
                          <button
                            onClick={applyFilters}
                            disabled={loading}
                            style={{
                              background: loading ? 'linear-gradient(135deg, #ccc 0%, #bbb 100%)' : 'linear-gradient(135deg, #ce55b9 0%, #b8479a 100%)',
                              color: 'white',
                              border: 'none',
                              borderRadius: '8px',
                              padding: '12px 24px',
                              cursor: loading ? 'not-allowed' : 'pointer',
                              fontSize: '14px',
                              fontWeight: '600',
                              fontFamily: '"Saira", sans-serif',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px',
                              opacity: loading ? 0.7 : 1,
                              transition: 'all 0.3s ease',
                              boxShadow: loading ? 'none' : '0 2px 8px rgba(206, 85, 185, 0.3)',
                              minWidth: '120px',
                              justifyContent: 'center'
                            }}
                            onMouseEnter={(e) => !loading && (e.target.style.transform = 'translateY(-1px)')}
                            onMouseLeave={(e) => (e.target.style.transform = 'translateY(0)')}
                          >
                            {loading && (
                              <span style={{ 
                                display: 'inline-block',
                                width: '14px',
                                height: '14px',
                                border: '2px solid transparent',
                                borderTop: '2px solid white',
                                borderRadius: '50%',
                                animation: 'spin 1s linear infinite'
                              }} />
                            )}
                            {loading ? 'Searching...' : '🔍 Apply Filters'}
                          </button>

                          <button
                            onClick={clearFilters}
                            disabled={loading}
                            style={{
                              background: loading ? 'linear-gradient(135deg, #ccc 0%, #bbb 100%)' : 'linear-gradient(135deg, #ff9800 0%, #f57c00 100%)',
                              color: 'white',
                              border: 'none',
                              borderRadius: '8px',
                              padding: '12px 24px',
                              cursor: loading ? 'not-allowed' : 'pointer',
                              fontSize: '14px',
                              fontWeight: '600',
                              fontFamily: '"Saira", sans-serif',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px',
                              opacity: loading ? 0.7 : 1,
                              transition: 'all 0.3s ease',
                              boxShadow: loading ? 'none' : '0 2px 8px rgba(255, 152, 0, 0.3)',
                              minWidth: '100px',
                              justifyContent: 'center'
                            }}
                            onMouseEnter={(e) => !loading && (e.target.style.transform = 'translateY(-1px)')}
                            onMouseLeave={(e) => (e.target.style.transform = 'translateY(0)')}
                          >
                            🗑️ Clear All
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Skeleton Loaders */}
                  {showSkeletonLoaders && (
                    <div style={{ marginTop: '32px' }}>
                      {[1, 2, 3, 4, 5].map(i => (
                        <SkeletonLoader key={i} />
                      ))}
                    </div>
                  )}

                  {/* Jobs List */}
                  {!isAnyLoading && !showSkeletonLoaders && !currentError && currentData.length > 0 && (
                    <div style={{ marginTop: '32px' }}>
                      <div style={{ display: 'grid', gap: '16px' }}>
                        {currentData.map((job) => (
                          <div
                            key={job.id}
                            style={{
                              backgroundColor: 'white',
                              border: '1px solid #e9ecef',
                              borderRadius: '12px',
                              padding: '24px',
                              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                              transition: 'all 0.3s ease',
                              ...(hasAppliedToJob(job) && {
                                borderColor: '#4caf50',
                                backgroundColor: '#f1f8e9'
                              }),
                              ...(hasSavedCurrentJob(job) && !hasAppliedToJob(job) && {
                                borderColor: '#ff9800',
                                backgroundColor: '#fff3e0'
                              })
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
                              e.currentTarget.style.transform = 'translateY(-2px)';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
                              e.currentTarget.style.transform = 'translateY(0)';
                            }}
                          >
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
                              {/* Company Logo + Job Info Section */}
                              <div 
                                onClick={() => handleJobClick(job.id)}
                                style={{ cursor: 'pointer', flex: 1, display: 'flex', alignItems: 'flex-start', gap: '16px' }}
                              >
                                {/* Company Logo */}
                                {job.company_logo && (
                                  <div style={{
                                    width: '60px',
                                    height: '60px',
                                    flexShrink: 0,
                                    position: 'relative',
                                    borderRadius: '8px',
                                    overflow: 'hidden',
                                    backgroundColor: '#f8f9fa',
                                    border: '1px solid #e9ecef'
                                  }}>
                                    {imageLoadingStates[job.id] === true && (
                                      <div style={{
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        right: 0,
                                        bottom: 0,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        backgroundColor: '#f8f9fa'
                                      }}>
                                        <div style={{
                                          width: '20px',
                                          height: '20px',
                                          border: '2px solid #e9ecef',
                                          borderTop: '2px solid #ce55b9',
                                          borderRadius: '50%',
                                          animation: 'spin 1s linear infinite'
                                        }} />
                                      </div>
                                    )}
                                    <img
                                      src={job.company_logo}
                                      alt={`${job.employer} logo`}
                                      style={{
                                        width: '100%',
                                        height: '100%',
                                        objectFit: 'contain',
                                        opacity: imageLoadingStates[job.id] === true ? 0 : 1,
                                        transition: 'opacity 0.3s ease'
                                      }}
                                      onLoad={() => handleImageLoad(job.id)}
                                      onError={() => handleImageError(job.id)}
                                    />
                                    {imageLoadingStates[job.id] === 'error' && (
                                      <div style={{
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        right: 0,
                                        bottom: 0,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        backgroundColor: '#f8f9fa',
                                        color: '#666',
                                        fontSize: '12px'
                                      }}>
                                        🏢
                                      </div>
                                    )}
                                  </div>
                                )}

                                {/* Job Info */}
                                <div style={{ flex: 1, minWidth: 0 }}>
                                  <h4 style={{
                                    fontSize: '20px',
                                    fontWeight: '600',
                                    color: '#333',
                                    marginBottom: '8px',
                                    fontFamily: '"Saira", sans-serif',
                                    wordWrap: 'break-word'
                                  }}>
                                    {job.job_title}
                                    {hasAppliedToJob(job) && (
                                      <span style={{
                                        marginLeft: '8px',
                                        fontSize: '12px',
                                        backgroundColor: '#4caf50',
                                        color: 'white',
                                        padding: '2px 8px',
                                        borderRadius: '12px',
                                        fontWeight: '500'
                                      }}>
                                        ✓ Applied
                                      </span>
                                    )}
                                    {hasSavedCurrentJob(job) && !hasAppliedToJob(job) && (
                                      <span style={{
                                        marginLeft: '8px',
                                        fontSize: '12px',
                                        backgroundColor: '#ff9800',
                                        color: 'white',
                                        padding: '2px 8px',
                                        borderRadius: '12px',
                                        fontWeight: '500'
                                      }}>
                                        💾 Saved
                                      </span>
                                    )}
                                  </h4>
                                  <p style={{
                                    fontSize: '16px',
                                    color: '#ce55b9',
                                    fontWeight: '500',
                                    margin: '0',
                                    fontFamily: '"Saira", sans-serif'
                                  }}>
                                    {job.employer}
                                  </p>
                                </div>
                              </div>
                              
                              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                {!hasAppliedToJob(job) ? (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      applyForJob(job.id);
                                    }}
                                    disabled={applyLoading}
                                    style={{
                                      background: '#ce55b9',
                                      color: 'white',
                                      border: 'none',
                                      borderRadius: '6px',
                                      padding: '6px 12px',
                                      cursor: applyLoading ? 'not-allowed' : 'pointer',
                                      fontSize: '12px',
                                      fontWeight: '500',
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: '4px',
                                      opacity: applyLoading ? 0.6 : 1,
                                      transition: 'all 0.2s ease'
                                    }}
                                    title="Apply for this job"
                                  >
                                    {applyLoading ? '⏳' : '📝 Apply'}
                                  </button>
                                ) : (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      withdrawJobApplication(job.id);
                                    }}
                                    disabled={withdrawLoading}
                                    style={{
                                      background: '#f44336',
                                      color: 'white',
                                      border: 'none',
                                      borderRadius: '6px',
                                      padding: '6px 12px',
                                      cursor: withdrawLoading ? 'not-allowed' : 'pointer',
                                      fontSize: '12px',
                                      fontWeight: '500',
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: '4px',
                                      opacity: withdrawLoading ? 0.6 : 1,
                                      transition: 'all 0.2s ease'
                                    }}
                                    title="Withdraw application"
                                  >
                                    {withdrawLoading ? '⏳' : '↩️ Withdraw'}
                                  </button>
                                )}

                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    toggleSaveJob(job.id);
                                  }}
                                  disabled={isJobSaveLoading(job.id)}
                                  style={{
                                    background: hasSavedCurrentJob(job) ? '#ff9800' : 'transparent',
                                    color: hasSavedCurrentJob(job) ? 'white' : '#666',
                                    border: hasSavedCurrentJob(job) ? 'none' : '1px solid #ddd',
                                    borderRadius: '6px',
                                    padding: '6px 8px',
                                    cursor: isJobSaveLoading(job.id) ? 'not-allowed' : 'pointer',
                                    fontSize: '14px',
                                    fontWeight: '500',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '4px',
                                    opacity: isJobSaveLoading(job.id) ? 0.6 : 1,
                                    transition: 'all 0.2s ease'
                                  }}
                                  title={hasSavedCurrentJob(job) ? 'Remove from saved' : 'Save job'}
                                >
                                  {isJobSaveLoading(job.id) ? '⏳' : hasSavedCurrentJob(job) ? '💾' : '🔖'}
                                </button>
                                
                                <div style={{
                                  backgroundColor: '#f0f9ff',
                                  color: '#0369a1',
                                  padding: '4px 12px',
                                  borderRadius: '20px',
                                  fontSize: '14px',
                                  fontWeight: '500',
                                  fontFamily: '"Saira", sans-serif'
                                }}>
                                  {getJobTypeLabel(job.job_type)}
                                </div>
                              </div>
                            </div>

                            <div onClick={() => handleJobClick(job.id)} style={{ cursor: 'pointer' }}>
                              <p style={{
                                fontSize: '14px',
                                color: '#666',
                                lineHeight: '1.5',
                                marginBottom: '16px',
                                fontFamily: '"Saira", sans-serif'
                              }}>
                                {job.job_description}
                              </p>

                              <div style={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                gap: '16px',
                                alignItems: 'center'
                              }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                  <span style={{ fontSize: '14px' }}>📍</span>
                                  <span style={{
                                    fontSize: '14px',
                                    color: '#666',
                                    fontFamily: '"Saira", sans-serif'
                                  }}>
                                    {job.location}
                                  </span>
                                </div>
                                
                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                  <span style={{ fontSize: '14px' }}>💰</span>
                                  <span style={{
                                    fontSize: '14px',
                                    color: '#666',
                                    fontFamily: '"Saira", sans-serif'
                                  }}>
                                    {getSalaryRangeLabel(job.salary_range)}
                                  </span>
                                </div>

                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                  <span style={{ fontSize: '14px' }}>🕒</span>
                                  <span style={{
                                    fontSize: '14px',
                                    color: '#666',
                                    fontFamily: '"Saira", sans-serif'
                                  }}>
                                    {job.hours_per_week}h/week
                                  </span>
                                </div>

                                <div style={{ marginLeft: 'auto' }}>
                                  <span style={{
                                    fontSize: '12px',
                                    color: '#999',
                                    fontFamily: '"Saira", sans-serif'
                                  }}>
                                    Posted {job.created_at_human || job.posted}
                                  </span>
                                </div>
                                
                                <div style={{
                                  backgroundColor: '#ce55b9',
                                  color: 'white',
                                  padding: '4px 12px',
                                  borderRadius: '12px',
                                  fontSize: '12px',
                                  fontWeight: '500',
                                  fontFamily: '"Saira", sans-serif',
                                  boxShadow: '0 2px 4px rgba(206, 85, 185, 0.3)'
                                }}>
                                  Click to view details
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>

                      {/* Pagination */}
                      {currentView === 'all' && pagination && pagination.last_page > 1 && (
                        <div style={{
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          gap: '12px',
                          marginTop: '32px',
                          padding: '20px'
                        }}>
                          <button
                            onClick={() => handlePageChange(currentPage - 1)}
                            disabled={currentPage === 1 || loading}
                            style={{
                              padding: '8px 16px',
                              backgroundColor: currentPage === 1 || loading ? '#f5f5f5' : '#ce55b9',
                              color: currentPage === 1 || loading ? '#999' : 'white',
                              border: 'none',
                              borderRadius: '6px',
                              cursor: currentPage === 1 || loading ? 'not-allowed' : 'pointer',
                              fontFamily: '"Saira", sans-serif',
                              opacity: loading ? 0.6 : 1
                            }}
                          >
                            Previous
                          </button>

                          <span style={{
                            padding: '8px 16px',
                            color: '#666',
                            fontFamily: '"Saira", sans-serif'
                          }}>
                            Page {pagination.current_page} of {pagination.last_page}
                          </span>

                          <button
                            onClick={() => handlePageChange(currentPage + 1)}
                            disabled={currentPage === pagination.last_page || loading}
                            style={{
                              padding: '8px 16px',
                              backgroundColor: currentPage === pagination.last_page || loading ? '#f5f5f5' : '#ce55b9',
                              color: currentPage === pagination.last_page || loading ? '#999' : 'white',
                              border: 'none',
                              borderRadius: '6px',
                              cursor: currentPage === pagination.last_page || loading ? 'not-allowed' : 'pointer',
                              fontFamily: '"Saira", sans-serif',
                              opacity: loading ? 0.6 : 1
                            }}
                          >
                            Next
                          </button>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Empty State */}
                  {!isAnyLoading && !showSkeletonLoaders && !currentError && currentData.length === 0 && (
                    <div style={{
                      padding: '64px 32px',
                      textAlign: 'center',
                      backgroundColor: '#f8f9fa',
                      borderRadius: '12px',
                      border: '1px solid #e9ecef',
                      marginTop: '32px'
                    }}>
                      <div style={{ fontSize: '64px', marginBottom: '24px' }}>
                        {currentView === 'saved' ? '💾' : isFiltering ? '🔍' : '🔍'}
                      </div>
                      <h4 style={{
                        fontSize: '24px',
                        fontWeight: 'bold',
                        color: '#333',
                        marginBottom: '16px',
                        fontFamily: '"Saira", sans-serif'
                      }}>
                        {currentView === 'saved' ? 'No Saved Jobs' : isFiltering ? 'No Jobs Match Your Filters' : 'No Jobs Found'}
                      </h4>
                      <p style={{
                        fontSize: '16px',
                        color: '#666',
                        marginBottom: '32px',
                        fontFamily: '"Saira", sans-serif'
                      }}>
                        {currentView === 'saved' 
                          ? 'You haven\'t saved any jobs yet. Browse the job listings and save jobs that interest you!'
                          : isFiltering
                            ? 'Try adjusting your search criteria or clearing filters to see more results.'
                            : 'There are currently no job listings available. Please check back later!'
                        }
                      </p>
                      <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
                        {currentView === 'saved' ? (
                          <button
                            onClick={() => handleViewChange('all')}
                            style={{
                              backgroundColor: '#ce55b9',
                              color: 'white',
                              border: 'none',
                              borderRadius: '8px',
                              padding: '12px 24px',
                              cursor: 'pointer',
                              fontSize: '16px',
                              fontFamily: '"Saira", sans-serif',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px'
                            }}
                          >
                            🔍 Browse All Jobs
                          </button>
                        ) : isFiltering ? (
                          <>
                            <button
                              onClick={clearFilters}
                              disabled={isAnyLoading}
                              style={{
                                backgroundColor: '#ff9800',
                                color: 'white',
                                border: 'none',
                                borderRadius: '8px',
                                padding: '12px 24px',
                                cursor: isAnyLoading ? 'not-allowed' : 'pointer',
                                fontSize: '16px',
                                fontFamily: '"Saira", sans-serif',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                opacity: isAnyLoading ? 0.6 : 1
                              }}
                            >
                              🗑️ Clear Filters
                            </button>
                            <button
                              onClick={refreshData}
                              disabled={isAnyLoading}
                              style={{
                                backgroundColor: '#ce55b9',
                                color: 'white',
                                border: 'none',
                                borderRadius: '8px',
                                padding: '12px 24px',
                                cursor: isAnyLoading ? 'not-allowed' : 'pointer',
                                fontSize: '16px',
                                fontFamily: '"Saira", sans-serif',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                opacity: isAnyLoading ? 0.6 : 1
                              }}
                            >
                              🔄 {isAnyLoading ? 'Refreshing...' : 'Refresh Jobs'}
                            </button>
                          </>
                        ) : (
                          <button
                            onClick={refreshData}
                            disabled={isAnyLoading}
                            style={{
                              backgroundColor: '#ce55b9',
                              color: 'white',
                              border: 'none',
                              borderRadius: '8px',
                              padding: '12px 24px',
                              cursor: isAnyLoading ? 'not-allowed' : 'pointer',
                              fontSize: '16px',
                              fontFamily: '"Saira", sans-serif',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px',
                              opacity: isAnyLoading ? 0.6 : 1
                            }}
                          >
                            🔄 {isAnyLoading ? 'Refreshing...' : 'Refresh Jobs'}
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Enhanced Job Details Modal */}
      {showJobModal && (
        <div className="slide-overlay" onClick={closeJobModal}>
          <div 
            className="slide-panel"
            onClick={(e) => e.stopPropagation()}
            style={{ fontFamily: '"Saira", sans-serif' }}
          >
            {/* Enhanced Header */}
            <div style={{
              background: '#ce55b9',
              color: 'white',
              padding: '24px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexShrink: 0
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{
                  width: '48px',
                  height: '48px',
                  background: 'rgba(255, 255, 255, 0.15)',
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '20px'
                }}>
                  💼
                </div>
                <div>
                  <h2 style={{
                    margin: 0,
                    fontSize: '24px',
                    fontWeight: '600',
                    fontFamily: '"Saira", sans-serif'
                  }}>
                    Job Details
                  </h2>
                  <p style={{
                    margin: 0,
                    fontSize: '14px',
                    opacity: 0.9,
                    fontFamily: '"Saira", sans-serif'
                  }}>
                    Complete job information and apply
                  </p>
                </div>
              </div>
              <button
                onClick={closeJobModal}
                disabled={jobDetailsLoading}
                style={{
                  background: 'rgba(255, 255, 255, 0.15)',
                  border: 'none',
                  color: 'white',
                  fontSize: '24px',
                  cursor: jobDetailsLoading ? 'not-allowed' : 'pointer',
                  padding: '8px',
                  borderRadius: '8px',
                  width: '40px',
                  height: '40px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  opacity: jobDetailsLoading ? 0.6 : 1,
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => !jobDetailsLoading && (e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.25)')}
                onMouseLeave={(e) => (e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.15)')}
              >
                ×
              </button>
            </div>

            {/* Content */}
            <div style={{ 
              flex: 1, 
              overflow: 'auto',
              background: '#ffffff'
            }}>
              {jobDetailsLoading && (
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: '100%',
                  padding: '40px'
                }}>
                  {loadingGif ? (
                    <img
                      src={loadingGif}
                      alt="Loading..."
                      style={{ width: '60px', marginBottom: '16px' }}
                    />
                  ) : (
                    <div style={{ 
                      marginBottom: '16px', 
                      fontSize: '32px',
                      animation: 'spin 2s linear infinite' 
                    }}>⏳</div>
                  )}
                  <div style={{ 
                    fontSize: '18px', 
                    color: '#666', 
                    fontFamily: '"Saira", sans-serif',
                    fontWeight: 500 
                  }}>
                    Loading job details...
                  </div>
                  <div style={{ 
                    fontSize: '14px', 
                    color: '#999', 
                    fontFamily: '"Saira", sans-serif',
                    marginTop: '8px' 
                  }}>
                    Please wait while we fetch the information
                  </div>
                </div>
              )}

              {jobDetailsError && (
                <div style={{ padding: '24px' }}>
                  <div style={{
                    padding: '24px',
                    backgroundColor: '#ffebee',
                    border: '1px solid #f44336',
                    borderRadius: '12px',
                    textAlign: 'center',
                    fontFamily: '"Saira", sans-serif'
                  }}>
                    <div style={{ fontSize: '48px', marginBottom: '16px' }}>❌</div>
                    <h3 style={{ 
                      color: '#c62828', 
                      marginBottom: '8px', 
                      fontFamily: '"Saira", sans-serif',
                      fontSize: '20px',
                      fontWeight: '600' 
                    }}>
                      Failed to Load Job Details
                    </h3>
                    <p style={{ 
                      color: '#c62828', 
                      marginBottom: '20px', 
                      fontFamily: '"Saira", sans-serif',
                      lineHeight: 1.5 
                    }}>
                      {jobDetailsError}
                    </p>
                    <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
                      <button
                        onClick={() => fetchJobDetails(selectedJob?.id)}
                        style={{
                          backgroundColor: '#ce55b9',
                          color: 'white',
                          border: 'none',
                          borderRadius: '8px',
                          padding: '12px 20px',
                          cursor: 'pointer',
                          fontSize: '14px',
                          fontFamily: '"Saira", sans-serif',
                          fontWeight: 500,
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px'
                        }}
                      >
                        🔄 Retry
                      </button>
                      <button
                        onClick={closeJobModal}
                        style={{
                          backgroundColor: '#6c757d',
                          color: 'white',
                          border: 'none',
                          borderRadius: '8px',
                          padding: '12px 20px',
                          cursor: 'pointer',
                          fontSize: '14px',
                          fontFamily: '"Saira", sans-serif',
                          fontWeight: 500
                        }}
                      >
                        Close
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {selectedJob && !jobDetailsLoading && !jobDetailsError && (
                <div style={{ padding: '24px' }}>
                  {/* Job Header */}
                  <div style={{
                    backgroundColor: '#f8f9fa',
                    padding: '24px',
                    borderRadius: '12px',
                    marginBottom: '24px'
                  }}>
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      marginBottom: '16px',
                      flexWrap: 'wrap',
                      gap: '12px'
                    }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <h3 style={{
                          fontSize: '28px',
                          fontWeight: '600',
                          color: '#333',
                          marginBottom: '8px',
                          fontFamily: '"Saira", sans-serif',
                          wordWrap: 'break-word'
                        }}>
                          {selectedJob.job_title}
                          {hasAppliedToJob(selectedJob) && (
                            <span style={{
                              marginLeft: '12px',
                              fontSize: '14px',
                              backgroundColor: '#4caf50',
                              color: 'white',
                              padding: '4px 12px',
                              borderRadius: '16px',
                              fontWeight: '500',
                              display: 'inline-block',
                              marginTop: '8px'
                            }}>
                              ✓ Applied
                            </span>
                          )}
                          {hasSavedCurrentJob(selectedJob) && !hasAppliedToJob(selectedJob) && (
                            <span style={{
                              marginLeft: '12px',
                              fontSize: '14px',
                              backgroundColor: '#ff9800',
                              color: 'white',
                              padding: '4px 12px',
                              borderRadius: '16px',
                              fontWeight: '500',
                              display: 'inline-block',
                              marginTop: '8px'
                            }}>
                              💾 Saved
                            </span>
                          )}
                        </h3>
                        <p style={{
                          fontSize: '20px',
                          color: '#ce55b9',
                          fontWeight: '500',
                          margin: '0',
                          fontFamily: '"Saira", sans-serif'
                        }}>
                          {selectedJob.employer}
                        </p>
                      </div>
                      <div style={{
                        backgroundColor: '#e3f2fd',
                        color: '#1976d2',
                        padding: '8px 16px',
                        borderRadius: '20px',
                        fontSize: '14px',
                        fontWeight: '500',
                        fontFamily: '"Saira", sans-serif',
                        flexShrink: 0
                      }}>
                        {getJobTypeLabel(selectedJob.job_type)}
                      </div>
                    </div>

                    {/* Job Meta Information */}
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                      gap: '16px',
                      marginTop: '20px'
                    }}>
                      <div style={{
                        backgroundColor: 'white',
                        padding: '16px',
                        borderRadius: '8px',
                        border: '1px solid #e9ecef'
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                          <span style={{ fontSize: '16px' }}>📍</span>
                          <span style={{
                            fontSize: '12px',
                            color: '#666',
                            fontWeight: '500',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                            fontFamily: '"Saira", sans-serif'
                          }}>
                            Location
                          </span>
                        </div>
                        <span style={{
                          fontSize: '16px',
                          color: '#333',
                          fontWeight: '500',
                          fontFamily: '"Saira", sans-serif'
                        }}>
                          {selectedJob.location}
                        </span>
                      </div>

                      <div style={{
                        backgroundColor: 'white',
                        padding: '16px',
                        borderRadius: '8px',
                        border: '1px solid #e9ecef'
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                          <span style={{ fontSize: '16px' }}>💰</span>
                          <span style={{
                            fontSize: '12px',
                            color: '#666',
                            fontWeight: '500',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                            fontFamily: '"Saira", sans-serif'
                          }}>
                            Salary
                          </span>
                        </div>
                        <span style={{
                          fontSize: '16px',
                          color: '#333',
                          fontWeight: '500',
                          fontFamily: '"Saira", sans-serif'
                        }}>
                          {getSalaryRangeLabel(selectedJob.salary_range)}
                        </span>
                      </div>

                      <div style={{
                        backgroundColor: 'white',
                        padding: '16px',
                        borderRadius: '8px',
                        border: '1px solid #e9ecef'
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                          <span style={{ fontSize: '16px' }}>🕒</span>
                          <span style={{
                            fontSize: '12px',
                            color: '#666',
                            fontWeight: '500',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                            fontFamily: '"Saira", sans-serif'
                          }}>
                            Hours/Week
                          </span>
                        </div>
                        <span style={{
                          fontSize: '16px',
                          color: '#333',
                          fontWeight: '500',
                          fontFamily: '"Saira", sans-serif'
                        }}>
                          {selectedJob.hours_per_week} hours
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Job Description */}
                  <div style={{ marginBottom: '24px' }}>
                    <h4 style={{
                      fontSize: '20px',
                      fontWeight: '600',
                      color: '#333',
                      marginBottom: '16px',
                      fontFamily: '"Saira", sans-serif'
                    }}>
                      Job Description
                    </h4>
                    <div style={{
                      backgroundColor: '#f8f9fa',
                      padding: '20px',
                      borderRadius: '8px',
                      border: '1px solid #e9ecef'
                    }}>
                      <p style={{
                        fontSize: '16px',
                        color: '#444',
                        lineHeight: '1.6',
                        margin: '0',
                        fontFamily: '"Saira", sans-serif'
                      }}>
                        {selectedJob.job_description}
                      </p>
                    </div>
                  </div>

                  {/* Job Dates */}
                  <div style={{
                    backgroundColor: '#f0f9ff',
                    padding: '20px',
                    borderRadius: '8px',
                    border: '1px solid #e3f2fd',
                    marginBottom: '24px'
                  }}>
                    <h5 style={{
                      fontSize: '16px',
                      fontWeight: '600',
                      color: '#333',
                      marginBottom: '12px',
                      fontFamily: '"Saira", sans-serif'
                    }}>
                      📅 Job Information
                    </h5>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px' }}>
                      <div>
                        <span style={{
                          fontSize: '14px',
                          color: '#666',
                          fontWeight: '500',
                          fontFamily: '"Saira", sans-serif'
                        }}>
                          Posted: {selectedJob.created_at_human || selectedJob.posted}
                        </span>
                      </div>
                      {selectedJob.updated_at_human && (
                        <div>
                          <span style={{
                            fontSize: '14px',
                            color: '#666',
                            fontWeight: '500',
                            fontFamily: '"Saira", sans-serif'
                          }}>
                            Last Updated: {selectedJob.updated_at_human}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Enhanced Action Buttons Footer */}
            {selectedJob && !jobDetailsLoading && !jobDetailsError && (
              <div style={{
                padding: '20px 24px',
                backgroundColor: 'white',
                borderTop: '1px solid rgba(0,0,0,0.1)',
                display: 'flex',
                gap: '12px',
                justifyContent: 'center',
                flexWrap: 'wrap',
                flexShrink: 0
              }}>
                {!hasAppliedToJob(selectedJob) ? (
                  <button
                    onClick={() => applyForJob(selectedJob.id)}
                    disabled={applyLoading}
                    style={{
                      background: applyLoading ? 'linear-gradient(135deg, #ccc 0%, #bbb 100%)' : 'linear-gradient(135deg, #ce55b9 0%, #b8479a 100%)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '10px',
                      padding: '14px 32px',
                      cursor: applyLoading ? 'not-allowed' : 'pointer',
                      fontSize: '16px',
                      fontWeight: '600',
                      fontFamily: '"Saira", sans-serif',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                      opacity: applyLoading ? 0.7 : 1,
                      transition: 'all 0.3s ease',
                      boxShadow: applyLoading ? 'none' : '0 4px 15px rgba(206, 85, 185, 0.3)',
                      minWidth: '160px',
                      justifyContent: 'center'
                    }}
                    onMouseEnter={(e) => !applyLoading && (e.target.style.transform = 'translateY(-2px)')}
                    onMouseLeave={(e) => (e.target.style.transform = 'translateY(0)')}
                  >
                    {applyLoading && (
                      <span style={{ 
                        display: 'inline-block',
                        width: '18px',
                        height: '18px',
                        border: '2px solid transparent',
                        borderTop: '2px solid white',
                        borderRadius: '50%',
                        animation: 'spin 1s linear infinite'
                      }} />
                    )}
                    {applyLoading ? 'Applying...' : '📝 Apply Now'}
                  </button>
                ) : (
                  <button
                    onClick={() => withdrawJobApplication(selectedJob.id)}
                    disabled={withdrawLoading}
                    style={{
                      background: withdrawLoading ? 'linear-gradient(135deg, #ccc 0%, #bbb 100%)' : 'linear-gradient(135deg, #f44336 0%, #d32f2f 100%)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '10px',
                      padding: '14px 32px',
                      cursor: withdrawLoading ? 'not-allowed' : 'pointer',
                      fontSize: '16px',
                      fontWeight: '600',
                      fontFamily: '"Saira", sans-serif',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                      opacity: withdrawLoading ? 0.7 : 1,
                      transition: 'all 0.3s ease',
                      boxShadow: withdrawLoading ? 'none' : '0 4px 15px rgba(244, 67, 54, 0.3)',
                      minWidth: '180px',
                      justifyContent: 'center'
                    }}
                    onMouseEnter={(e) => !withdrawLoading && (e.target.style.transform = 'translateY(-2px)')}
                    onMouseLeave={(e) => (e.target.style.transform = 'translateY(0)')}
                  >
                    {withdrawLoading && (
                      <span style={{ 
                        display: 'inline-block',
                        width: '18px',
                        height: '18px',
                        border: '2px solid transparent',
                        borderTop: '2px solid white',
                        borderRadius: '50%',
                        animation: 'spin 1s linear infinite'
                      }} />
                    )}
                    {withdrawLoading ? 'Withdrawing...' : '↩️ Withdraw Application'}
                  </button>
                )}
                
                <button
                  onClick={() => toggleSaveJob(selectedJob.id)}
                  disabled={isJobSaveLoading(selectedJob.id)}
                  style={{
                    background: isJobSaveLoading(selectedJob.id) ? 
                      'linear-gradient(135deg, #ccc 0%, #bbb 100%)' : 
                      hasSavedCurrentJob(selectedJob) ? 
                        'linear-gradient(135deg, #ff9800 0%, #f57c00 100%)' : 
                        'linear-gradient(135deg, #6c757d 0%, #5a6268 100%)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '10px',
                    padding: '14px 32px',
                    cursor: isJobSaveLoading(selectedJob.id) ? 'not-allowed' : 'pointer',
                    fontSize: '16px',
                    fontWeight: '600',
                    fontFamily: '"Saira", sans-serif',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    opacity: isJobSaveLoading(selectedJob.id) ? 0.7 : 1,
                    transition: 'all 0.3s ease',
                    boxShadow: isJobSaveLoading(selectedJob.id) ? 'none' : 
                      hasSavedCurrentJob(selectedJob) ? '0 4px 15px rgba(255, 152, 0, 0.3)' : 
                      '0 4px 15px rgba(108, 117, 125, 0.3)',
                    minWidth: '160px',
                    justifyContent: 'center'
                  }}
                  onMouseEnter={(e) => !isJobSaveLoading(selectedJob.id) && (e.target.style.transform = 'translateY(-2px)')}
                  onMouseLeave={(e) => (e.target.style.transform = 'translateY(0)')}
                >
                  {isJobSaveLoading(selectedJob.id) && (
                    <span style={{ 
                      display: 'inline-block',
                      width: '18px',
                      height: '18px',
                      border: '2px solid transparent',
                      borderTop: '2px solid white',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite'
                    }} />
                  )}
                  {isJobSaveLoading(selectedJob.id) ? 
                    (hasSavedCurrentJob(selectedJob) ? 'Removing...' : 'Saving...') : 
                    hasSavedCurrentJob(selectedJob) ? '🗑️ Remove from Saved' : '💾 Save Job'
                  }
                </button>
                
                <button
                  onClick={closeJobModal}
                  style={{
                    background: 'linear-gradient(135deg, #6c757d 0%, #5a6268 100%)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '10px',
                    padding: '14px 32px',
                    cursor: 'pointer',
                    fontSize: '16px',
                    fontWeight: '600',
                    fontFamily: '"Saira", sans-serif',
                    transition: 'all 0.3s ease',
                    boxShadow: '0 4px 15px rgba(108, 117, 125, 0.3)',
                    minWidth: '120px'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.transform = 'translateY(-2px)';
                    e.target.style.background = 'linear-gradient(135deg, #5a6268 0%, #495057 100%)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.background = 'linear-gradient(135deg, #6c757d 0%, #5a6268 100%)';
                  }}
                >
                  Close
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default JobsList;